import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Search, Menu, X, TreePine } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings, getPhoneLines } from "@/lib/settings";

const NAV = [
  { label: "Domov", href: "/", testid: "nav-link-domov" },
  { label: "Trgovina", href: "/trgovina", testid: "nav-link-trgovina" },
  { label: "Razrez po meri", href: "/#razrez", testid: "nav-link-razrez" },
  { label: "O nas", href: "/#o-nas", testid: "nav-link-o-nas" },
  { label: "Kontakt", href: "/#kontakt", testid: "nav-link-kontakt" },
];

export default function Header() {
  const { count, setOpen } = useCart();
  const settings = useSettings();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/trgovina?q=${encodeURIComponent(q.trim())}` : "/trgovina");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-[#E4E0D7]/70 bg-[#FBF9F5]/85 backdrop-blur-xl">
      <div className="bg-[#1A1D1A] text-[#FBF9F5] text-xs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center justify-between font-tech tracking-wide">
          <span data-testid="topbar-shipping">{settings.obvestilo}</span>
          <span data-testid="topbar-phone" className="hidden sm:block">{getPhoneLines(settings).map((l) => (l.oddelek ? `${l.oddelek}: ${l.stevilka}` : l.stevilka)).join(" · ")} · {settings.odpiralni_cas}</span>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 h-16">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <span className="w-9 h-9 rounded-lg bg-[#2D4030] text-[#FBF9F5] flex items-center justify-center">
              <TreePine className="w-5 h-5" />
            </span>
            <span className="font-display font-extrabold text-xl tracking-tight text-[#1A1D1A]">
              Hobby<span className="text-[#B45309]">Center</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-6">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                data-testid={n.testid}
                className="px-3 py-2 text-sm font-medium text-[#3f463f] hover:text-[#B45309] transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-sm ml-auto">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                data-testid="search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Išči izdelke …"
                className="w-full h-10 pl-9 pr-4 rounded-full bg-white border border-[#E4E0D7] text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4030]/30"
              />
            </div>
          </form>

          <button
            data-testid="cart-drawer-toggle"
            onClick={() => setOpen(true)}
            className="relative ml-auto md:ml-0 flex items-center gap-2 h-10 px-4 rounded-full bg-[#2D4030] text-[#FBF9F5] text-sm font-semibold hover:bg-[#1E2C20] transition-colors"
          >
            <ShoppingCart className="w-4 h-4" />
            <span className="hidden sm:inline">Košarica</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#D97706] text-white text-xs font-bold flex items-center justify-center">
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 text-[#1A1D1A]"
            aria-label="Meni"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-[#E4E0D7] bg-[#FBF9F5] px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2.5 rounded-lg text-sm font-medium text-[#1A1D1A] hover:bg-[#F3EFE6]"
            >
              {n.label}
            </a>
          ))}
          <form onSubmit={submitSearch} className="pt-2">
            <input
              data-testid="search-input-mobile"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Išči izdelke …"
              className="w-full h-10 px-4 rounded-full bg-white border border-[#E4E0D7] text-sm focus:outline-none"
            />
          </form>
        </div>
      )}
    </header>
  );
}
